import { apiFetch, isApiMode } from './http';

// GET /me — who the API thinks you are, and what it says you may do.
//
// The shape mirrors apps/api/src/app.ts exactly. Note what `authorization`
// carries: ACTUAL grants read from Starpoint's own tables. A brand-new staff
// sign-in gets empty lists and `isPlayerOnly: true`, because being in the Entra
// directory proves employment, not entitlement. The browser renders this; it
// never computes it.

export interface MeAccount {
  id: string;
  status: string;
  displayName: string | null;
  email: string | null;
  /**
   * The UI language this account chose, or null for «never chosen» (#310).
   *
   * NULL AND 'nb' ARE DIFFERENT ANSWERS. «Never chosen» means this browser's
   * own stored preference still governs; 'nb' means the person asked for
   * Norwegian on some machine and that choice follows them here. Collapsing
   * the two would make an account setting silently overrule a browser one
   * for every user who has never opened the control.
   *
   * The server validates before storing and reports anything it does not
   * recognise as null, so this is a catalog key or nothing.
   */
  locale: string | null;
}

export interface MeAuthorization {
  platformRoles: string[];
  tenants: {
    tenantId: string;
    kind: string;
    roles: string[];
    /**
     * The union of those roles' capabilities in THIS organization (slice 3,
     * 2026-10-18) — what the server's evaluator reads, so the portal can draw
     * the doors this account may open (the «Tilganger» entry) without a
     * role-key check in the browser. For rendering only; the server
     * re-decides every call. Optional because an older API does not send it,
     * and absent reads as «unknown», which draws nothing.
     */
    capabilities?: string[];
  }[];
  /**
   * What a co-operation lets this account do INSIDE another organization
   * (slice 4b) — already met with its right at home by the server's own rule,
   * so it is exactly what the evaluator would answer. For drawing doors such
   * as «Book baner hos Padelsenteret»; the server re-decides every call.
   * Optional: an older API does not send it, and absent draws nothing.
   */
  links?: MeLink[];
  isPlayerOnly: boolean;
}

/** One co-operation as  reports it (slice 4b). */
export interface MeLink {
  linkId: string;
  /** The organization the link lets the account act IN. */
  hostTenantId: string;
  hostKind: string;
  hostSlug: string;
  hostName: string;
  /** The account's own organization the act is on behalf of. */
  homeTenantId: string;
  capabilities: string[];
}

export interface MeResponse {
  account: MeAccount;
  identity: { provider: string; subject: string };
  authorization: MeAuthorization;
  /**
   * The caller's platform capabilities (#924): what `can()` reads for a
   * platform-scoped guard. /sa keys each write control on one of these
   * (`saWriteCapabilities.ts`). Optional because an API older than the field
   * does not send it, and absent offers nothing.
   */
  platformCapabilities?: string[];
  /**
   * Whether this account administers bug reports (owner decision 2026-10-22):
   * a staff account whose address is on the deployment's allowlist. Decides
   * whether the floating button offers counts and the archive; every
   * administrative route re-decides. Optional: an older API does not send it,
   * and absent offers nothing.
   */
  bugAdmin?: boolean;
}

export type MeResult =
  /** The API is not wired up in this build (VITE_API_URL unset). */
  | { state: 'no-api' }
  | { state: 'ok'; me: MeResponse }
  /** Authenticated call refused — no token, or one the API would not accept. */
  | { state: 'unauthorized' }
  | { state: 'error'; status: number | null };

/**
 * The capability union `/me` reports for ONE organization, or null when it
 * cannot be known — no answer yet, a refused or failed call, an organization
 * the account holds no role in, or an older API that does not send the field.
 *
 * NULL AND EMPTY ARE DIFFERENT ANSWERS. Empty is the server saying «nothing
 * here»; null is the browser not knowing. A door is drawn only on a yes, so
 * both draw nothing — but a caller that wants to say «could not tell» can.
 */
export function organizationCapabilities(
  me: MeResult | null,
  tenantId: string | null,
): string[] | null {
  if (!me || me.state !== 'ok' || !tenantId) return null;
  const entry = me.me.authorization.tenants.find((t) => t.tenantId === tenantId);
  return entry?.capabilities ?? null;
}

/** The capability a club uses to book a centre's courts through a link (slice 4c). */
export const LINK_BOOKING_CREATE = 'link.booking.create';

/**
 * The centres the account may book courts at ON BEHALF OF `tenantId` — the
 * club the portal is acting in — through an active co-operation. Empty when
 * there are none, or when `/me` has not answered: no door is drawn on a guess.
 */
export function linkBookingHosts(me: MeResult | null, tenantId: string | null): MeLink[] {
  if (!me || me.state !== 'ok' || !tenantId) return [];
  return (me.me.authorization.links ?? []).filter(
    (l) => l.homeTenantId === tenantId && l.capabilities.includes(LINK_BOOKING_CREATE),
  );
}

/** The capability a centre uses to arrange activities in a club through a link (slice 4d). */
export const LINK_ACTIVITY_MANAGE = 'link.activity.manage';

/**
 * The clubs the account may arrange activities in ON BEHALF OF `tenantId` —
 * the centre the portal is acting in — through an active co-operation.
 */
export function linkActivityHosts(me: MeResult | null, tenantId: string | null): MeLink[] {
  if (!me || me.state !== 'ok' || !tenantId) return [];
  return (me.me.authorization.links ?? []).filter(
    (l) => l.homeTenantId === tenantId && l.capabilities.includes(LINK_ACTIVITY_MANAGE),
  );
}

/** Ask the API who we are. Never throws; the caller renders the outcome. */
export async function fetchMe(): Promise<MeResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch('/me');
    if (res.status === 401 || res.status === 403) return { state: 'unauthorized' };
    if (!res.ok) return { state: 'error', status: res.status };
    return { state: 'ok', me: (await res.json()) as MeResponse };
  } catch {
    return { state: 'error', status: null };
  }
}
